import prefixes from 'components/PhonePrefixField/prefixes.json';
import { APP_COUNTRY } from 'config';
import { selectVerifiedContactsApiConfig } from 'pages/profile/profile.selectors';
import { useCallback, useEffect } from 'react';
import { batch, useDispatch, useSelector, useStore } from 'react-redux';
import { useUserPersonalDataQuery } from 'services/account-ms/get-user-personal-data';
import { requestDataIfNecessary } from 'services/api/api.actions';
import {
  makeSelectGetGameBreakPeriodsApiConfig,
  makeSelectGetSummaryGameBreakApiConfig,
  selectAmountPlayedNotificationLoadState,
  selectGetGameBreakPeriodsLoadState,
  selectGetSummaryGameBreakLoadState,
  selectGetUserCommunicationsEndpointApiConfig,
} from 'services/playResponsibly/playResponsibly.selectors';
import { setStaticPrefixes } from 'services/prefix/prefix.action';
import {
  selectGetInfoPrivacyEndpointApiConfig,
  selectInfoPrivacyLoadState,
} from 'services/privacy/privacy.selectors';
import { selectSessionInfo } from 'services/session/session.selectors';
import {
  selectLastAccessDate,
  selectPinCodeApiConfig,
} from 'services/user/user.selectors';
import {
  SandsivSurveyIdPS,
  useSandsivTriggerPs,
} from 'utils/hooks/sandsiv/use-survey-trigger-ps';
import useGetTranslate from 'utils/hooks/useGetTranslate';
import {
  selectGetDocumentExpirationDaysApiConfig,
  selectGetDocumentExpirationDaysLoadState,
} from './dashboard.helpers';

export function useDashboardTitleConfig() {
  const lastAccessDate: string = useSelector(selectLastAccessDate);
  const translate = useGetTranslate();

  if (['pt', 'es'].includes(APP_COUNTRY)) {
    return {
      title: translate('text.dashboard.title') as string,
    };
  }

  return {
    title: translate('text.dashboard.title') as string,
    subTitle: translate('text.dashboard.subtitle') + ' ' + lastAccessDate,
  };
}

export function useManageApiDataRequiredForAlertBar() {
  const { getState } = useStore();
  const dispatch = useDispatch();

  const getDocumentExpirationDaysLoadState = useSelector(
    selectGetDocumentExpirationDaysLoadState,
  );

  useEffect(() => {
    const getExpirationDays =
      selectGetDocumentExpirationDaysApiConfig(getState());
    dispatch(requestDataIfNecessary(getExpirationDays));
  }, [dispatch, getState]);

  return {
    hasLoadedAll: getDocumentExpirationDaysLoadState.isLoaded,
    hasInvalidatedData: getDocumentExpirationDaysLoadState.isInvalidated,
    hasFailedToLoad: getDocumentExpirationDaysLoadState.hasFailed,
  };
}

export function useScrollToHash(dependency: boolean) {
  useEffect(() => {
    if (dependency) {
      const hash = window.location.hash;

      if (hash) {
        const sectionId = hash.substring(1);
        const sectionElement = document.getElementById(sectionId);

        if (sectionElement) {
          const sectionRect = sectionElement.getBoundingClientRect();
          const topPosition = sectionRect.top + window.scrollY - 50;

          window.scrollTo({ top: topPosition, behavior: 'smooth' });
        }
      }
    }
  }, [dependency]);
}

export function useFeedbackFormOpener() {
  const sessionInfo = useSelector(selectSessionInfo);
  const { ipAddress } = sessionInfo;

  const triggerSurveyPs = useSandsivTriggerPs();

  const openFeedbackForm = useCallback(() => {
    triggerSurveyPs(SandsivSurveyIdPS.GiveFeedback, 'cta', ipAddress);
  }, [triggerSurveyPs, ipAddress]);

  return openFeedbackForm;
}

export const useRequiredApiDataForDashoardSeuPage = () => {
  const { getState } = useStore();
  const dispatch = useDispatch();

  useEffect(() => {
    const state = getState();
    const pinCodeApiConfig = selectPinCodeApiConfig(state);
    dispatch(requestDataIfNecessary(pinCodeApiConfig));
  }, [dispatch, getState]);

  const hasLoadedAll = true;

  return { hasLoadedAll };
};

export function useManageApiDataRequiredForPlayResponsabilitySeuPage() {
  const reduxStore = useStore();
  const {
    isLoaded: isUserPersonalDataLoaded,
    hasFailed: isUserPersonalDataFailed,
  } = useUserPersonalDataQuery();
  const getSummaryGameBreakLoadState = useSelector(
    selectGetSummaryGameBreakLoadState,
  );

  const getGameBreakPeriodsLoadState = useSelector(
    selectGetGameBreakPeriodsLoadState,
  );

  const getBalanceNotificationLoadState = useSelector(
    selectAmountPlayedNotificationLoadState,
  );

  const getInfoPrivacyLoadState = useSelector(selectInfoPrivacyLoadState);

  useEffect(() => {
    const verifiedContactsApiConfig = selectVerifiedContactsApiConfig(
      reduxStore.getState(),
    );

    const getBalanceNotificationApiConfig =
      selectGetUserCommunicationsEndpointApiConfig(reduxStore.getState());

    const getInfoPrivacyApiConfig = selectGetInfoPrivacyEndpointApiConfig(
      reduxStore.getState(),
    );

    const getSummaryGameBreakApiConfig =
      makeSelectGetSummaryGameBreakApiConfig()(reduxStore.getState());

    const getGameBreakPeriodsApiConfig =
      makeSelectGetGameBreakPeriodsApiConfig()(reduxStore.getState());

    batch(() => {
      reduxStore.dispatch(
        requestDataIfNecessary(getBalanceNotificationApiConfig),
      );
      reduxStore.dispatch(requestDataIfNecessary(getInfoPrivacyApiConfig));

      reduxStore.dispatch(requestDataIfNecessary(getSummaryGameBreakApiConfig));

      reduxStore.dispatch(requestDataIfNecessary(getGameBreakPeriodsApiConfig));

      reduxStore.dispatch(requestDataIfNecessary(verifiedContactsApiConfig));

      reduxStore.dispatch(setStaticPrefixes(Object.values(prefixes)));
    });
  }, [reduxStore]);

  return {
    hasLoadedAll:
      getInfoPrivacyLoadState.isLoaded &&
      getBalanceNotificationLoadState.isLoaded &&
      (getSummaryGameBreakLoadState.isLoaded ||
        getSummaryGameBreakLoadState.hasFailed) &&
      (getGameBreakPeriodsLoadState.isLoaded ||
        getGameBreakPeriodsLoadState.hasFailed) &&
      isUserPersonalDataLoaded &&
      !isUserPersonalDataFailed,

    hasInvalidatedData:
      getInfoPrivacyLoadState.isInvalidated ||
      getBalanceNotificationLoadState.isInvalidated ||
      getSummaryGameBreakLoadState.isInvalidated ||
      getGameBreakPeriodsLoadState.isInvalidated,

    hasFailedToLoad:
      getInfoPrivacyLoadState.hasFailed ||
      getBalanceNotificationLoadState.hasFailed ||
      isUserPersonalDataFailed,
  };
}
